import Cookies from "js-cookie";
import {DataGrid} from "@mui/x-data-grid";
import {routes, useGetDataList, useGroupUpdateData, useSearch} from "../routes/api";
import React, {useContext, useEffect, useLayoutEffect, useState} from "react";
import {LoadingComponent, NoDataComponent} from "../util/loading";
import {useLocation, useNavigate} from "react-router-dom";
import Button from "@mui/material/Button";
import {Box, ButtonGroup, Grid, MenuItem, Select} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from '@mui/icons-material/Edit';
import {CameraListHeaders} from "../util/getFields";
import RelationView from "../components/relationView";
import PopupDialog from "../components/popupDialog";
import Typography from "@mui/material/Typography";
import ErrorDisplay from "../components/errorDisplay";
import NavigateBreadcrumbs from "../components/navBreadcrumbs";
import Tooltip from "@mui/material/Tooltip";
import SensorsIcon from "@mui/icons-material/Sensors";
import {green, red} from "@mui/material/colors";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import SearchBox from "../components/searchBox";
import {cameraSearchFields} from "../util/searchFields";
import {EnumContext} from "../components/enumContext";
import {PAGE_SIZE, updateData} from "../util/util";
import WaitTimeStatus from "../components/waitTimeStatus";
import {cameraFields} from "../util/modelFields";
import CustomToolbar from "../components/gridToolbar";
import CaptureSettings from "../components/captureSettings";

export default function CameraList() {
    const token = Cookies.get('token');
    const permissions = Cookies.get('permissions') ?
        JSON.parse(Cookies.get('permissions')).unit :
        {create: false, edit: false, delete: false, view: false};
    const webAdmin = Cookies.get('webAdmin') === 'T';
    const route = routes.camera;
    const groupRoute = routes.cameragroup;
    const {enums} = useContext(EnumContext);

    const [snackbarList, setSnackbarList] = useState([]);
    const [noData, setNoData] = useState(false);
    const [viewHeaders, setViewHeaders] = useState([
        ...CameraListHeaders.filter(key => key !== 'id').map((key) =>
            ({
                field: key,
                headerName: cameraFields.fields[key].display,
                type: cameraFields.fields[key].type === 'boolean' ? 'boolean' : 'string',
                minWidth: 100,
                flex: 1,
                hide: key === 'id',
                hideable: key !== 'c_sn',
                valueGetter: (value) => (Array.isArray(value) ?
                        value.reduce((acc, item) => (acc ? `${acc}, ${item}` : item), '') :
                        value
                )
            })),
        {
            field: 'iconStatus',
            headerName: 'Status',
            headerAlign: 'center',
            cellClassName: 'centeredCell',
            width: 150,
            renderCell: (params) => (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <Tooltip
                        title={`Status: ${(params.row.c_error && params.row.c_error.length > 0) ? params.row.c_error : 'No Issue'}`}>
                        {(params.row.c_error && params.row.c_error.length > 0) ? (
                            <SensorsIcon style={{color: red[300]}}/>
                        ) : (
                            <SensorsIcon style={{color: green[300]}}/>
                        )}
                    </Tooltip>
                </Box>
            ),
        }
    ]);

    const [rowSelected, setRowSelected] = useState([]);
    const [aiModelSelect, setAIModelSelect] = useState(false);
    const [aiModelId, setAIModelId] = useState(null);
    const [alert, setAlert] = useState(false);
    const [newData, setNewData] = useState([]);

    const {data, loading, statusCode: status, handleCall} = useGetDataList(token, route);
    const {data: groupResp, statusCode: groupStatus, handleCall: groupCall} = useGroupUpdateData(token, groupRoute);
    const {
        data: searchData,
        loading: searchLoading,
        statusCode: searchStatus,
        handleCall: searchCall
    } = useSearch(token, route);


    const [totalPages, setTotalPages] = useState(null);


    const location = useLocation();
    const navigate = useNavigate();
    const from = location.state?.from ? location.state.from : {pathname: `/`};

    const [searchParams, setSearchParams] = useState({});
    const [searching, setSearching] = useState(false);
    const [openSearch, setOpenSearch] = useState(false);
    const [captureSettingsUpdate, setCaptureSettingsUpdate] = useState(false);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({
        c_part: false,
        c_status: false,
        c_timeAdded: false,
    });

    const [filteredData, setFilteredData] = useState([]);

    const [paginationModel, setPaginationModel] = useState({
        page: -1,
        pageSize: PAGE_SIZE,
    });

    useLayoutEffect(() => {
        if (location?.state?.from?.pathname?.split('/')[1] === 'camera' || !location?.state?.from?.pathname) {
            setPaginationModel({...paginationModel, page: Number(sessionStorage.getItem('cameraPage'))});
            setSearchParams(JSON.parse(sessionStorage.getItem('cameraSearchParams')) || {});
            setSearching(sessionStorage.getItem('cameraSearching') === 'T');
            setOpenSearch(sessionStorage.getItem('cameraOpenSearch') === 'T');
            setColumnVisibilityModel(JSON.parse(sessionStorage.getItem('cameraColumns')) || {
                c_part: false,
                c_status: false,
                c_timeAdded: false,
            });
        } else {
            setPaginationModel({...paginationModel, page: 0});
            sessionStorage.setItem('cameraPage', '0');
            sessionStorage.setItem('cameraSearchParams', '{}');
            sessionStorage.setItem('cameraSearching', 'F');
            sessionStorage.setItem('cameraOpenSearch', 'F');
            sessionStorage.setItem('cameraColumns', JSON.stringify({
                c_part: false,
                c_status: false,
                c_timeAdded: false,
            }));
        }
    }, []);

    const handleReset = () => {
        setColumnVisibilityModel({
            c_part: false,
            c_status: false,
            c_timeAdded: false,
        });
        sessionStorage.setItem('cameraColumns', JSON.stringify({
            c_part: false,
            c_status: false,
            c_timeAdded: false,
        }));
    };

    const fetchData = async () => {
        try {
            if (searching) {
                await searchCall({params: {...searchParams, page: paginationModel.page + 1}});
            } else {
                await handleCall({params: {page: paginationModel.page + 1}});
            }
        } catch (error) {
            console.error('Error fetching camera data:', error);
        }
    }

    useLayoutEffect(() => {
        if (!(permissions.view || webAdmin)) {
            navigate(from);
        }
    }, []);

    useEffect(() => {
        paginationModel.page >= 0 && fetchData();
    }, [paginationModel]);

    useEffect(() => {
        if (status === 204 || data?.count === 0 || data?.results?.length === 0) {
            setNoData(true);
        } else if (data?.results?.length > 0) {
            const updatedData = data.results.map(data => updateData(data, null, 'c', enums));
            setNewData(updatedData);
            setNoData(false);
            setTotalPages(Math.ceil(data.count / PAGE_SIZE));
        } else {
            setNoData(false);
        }
    }, [data, status])

    useEffect(() => {
        if (searchData.results?.length > 0) {
            setFilteredData(searchData.results.map(data => updateData(data, null, 'c', enums)));
            setTotalPages(Math.ceil(searchData.count / PAGE_SIZE));
        } else {
            setFilteredData([]);
            setTotalPages(0);
        }
    }, [searchData, searchStatus]);

    const handleColumnResize = (params) => {
        setViewHeaders((prevColumns) =>
            prevColumns.map((col) =>
                col.field === params.colDef.field
                    ? {...col, width: params.width}
                    : col
            )
        );
    };


    const handleRowClick = (param, event) => {
        event.preventDefault();
        window.history.replaceState({from: location}, '');
        navigate(`/camera/${param.row.id}`, {state: {from: location}});
    };

    const handleEntry = (event) => {
        event.preventDefault();
        navigate(`/camera/new`, {state: {from: location}});
    };

    const handleRelationChange = (newValue, key, multiple) => {
        if (multiple) {
            setAIModelId(newValue.map((item) => item[0]));
        } else if (!newValue) {
            setAIModelId(null);
        } else {
            setAIModelId(newValue[0]);
        }
    }

    const handleAIModelSubmit = async () => {
        const body = {cameras: rowSelected, aimodel: aiModelId};
        await groupCall({body: body});
        setAIModelSelect(false);
    }

    const addSnackbar = (field, model, time) => {
        const id = new Date().getTime();
        setSnackbarList((prevSnackbars) => [
            ...prevSnackbars,
            {id, field, model, time},
        ]);
    };

    const handleClose = (id, event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarList((prevSnackbars) => prevSnackbars.filter((snackbar) => snackbar.id !== id));
    };

    const handleSearch = async () => {
        setFilteredData([]);
        setSearching(true);
        sessionStorage.setItem('cameraSearching', 'T')
        setPaginationModel((pm) => ({...pm, page: 0}));
        sessionStorage.setItem('cameraPage', '0');
    }

    const handleOpenSearch = () => {
        setOpenSearch(true);
        sessionStorage.setItem('cameraOpenSearch', 'T');
    }

    const handleCancelSearch = () => {
        if (searching) {
            setPaginationModel((pm) => ({...pm, page: 0}));
            sessionStorage.setItem('cameraPage', '0');
        }
        setOpenSearch(false);
        sessionStorage.setItem('cameraOpenSearch', 'F');
        setSearching(false);
        sessionStorage.setItem('cameraSearching', 'F');
        setSearchParams({});
        sessionStorage.setItem('cameraSearchParams', '{}');
        setFilteredData([]);
    }

    const handlePageSelect = (e) => {
        setPaginationModel((pm) => ({...pm, page: e.target.value}));
        sessionStorage.setItem('cameraPage', e.target.value);
    }

    const handlePaginationModelUpdate = (model, details) => {
        setPaginationModel(model);
        sessionStorage.setItem('cameraPage', model.page);
    }

    const handleSearchParams = (params) => {
        sessionStorage.setItem('cameraSearchParams', JSON.stringify(params));
        setSearchParams(params);
    }

    const handleColumnVisibilityLabel = (columns) => {
        sessionStorage.setItem('cameraColumns', JSON.stringify(columns));
        setColumnVisibilityModel(columns);
    }

    useEffect(() => {
        if (groupStatus === 202) {
            setAlert(false);
            setAIModelSelect(false);
            addSnackbar('Batch updating AI Model', 'Camera', '10 minutes');
        } else if (groupStatus === 207) {
            setAlert(true);
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }, [groupResp, groupStatus]);

    if ((!searching && loading) || (searching && searchLoading)) return <LoadingComponent/>;
    if (noData) return (
        <Grid
            container
            spacing={2}
            sx={{margin: 'auto', mt: 5, width: '90%'}}
            justify="center"
        >
            <Grid size={12}/>
            <Grid size={12}>
                <NavigateBreadcrumbs alias='camera' display='Camera'/>
            </Grid>
            <Grid size={9}>
                <Typography variant='h4'>Cameras</Typography>
            </Grid>
            {(permissions.create || webAdmin) && <Grid size={3} sx={{display: "flex", justifyContent: "flex-end"}}>
                <Button component="label"
                        variant="contained"
                        onClick={handleEntry}
                        startIcon={<AddIcon/>}
                >
                    New Camera
                </Button>
            </Grid>}
            <Grid size={12}>
                <NoDataComponent/>
            </Grid>
        </Grid>
    )

    return (
        <Grid
            container
            spacing={2}
            sx={{margin: 'auto', mt: 5, width: '90%'}}
            justifyContent="space-between"
        >
            <Grid size={12}>
                <ErrorDisplay
                    alert={alert}
                    setAlert={setAlert}
                    response={groupResp}
                    statusCode={groupStatus}
                    batch
                />
                <WaitTimeStatus
                    snackbarList={snackbarList}
                    handleClose={handleClose}
                />
            </Grid>
            <Grid size={12}>
                <NavigateBreadcrumbs alias='camera' display='Camera'/>
            </Grid>
            <Grid size={9}>
                <Typography variant='h4'>Cameras</Typography>
            </Grid>
            {(permissions.create || webAdmin) &&
                <Grid size={3} sx={{display: "flex", justifyContent: "flex-end"}}>
                    <Button
                        component="label"
                        variant="contained"
                        onClick={handleEntry}
                        startIcon={<AddIcon/>}>
                        New Camera
                    </Button>
                </Grid>}
            <Grid size='auto'>
                {(permissions.edit || webAdmin) &&
                    <ButtonGroup disabled={rowSelected.length <= 0} sx={{mb: 1}}>
                        <Button startIcon={<EditIcon/>} onClick={() => setAIModelSelect(true)}>
                            AI Model
                        </Button>
                        <Button startIcon={<EditIcon/>} onClick={() => setCaptureSettingsUpdate(true)}>
                            Capture Settings
                        </Button>
                    </ButtonGroup>}
            </Grid>
            <Grid size='grow'/>
            <Grid size={3} sx={{display: "flex", justifyContent: "flex-end"}}>
                <Button
                    component="label"
                    onClick={openSearch ? handleCancelSearch : handleOpenSearch}
                    startIcon={<ManageSearchIcon/>}
                    sx={{mb: 1}}>
                    {openSearch ? "Cancel" : "Filters"}
                </Button>
            </Grid>
            {openSearch && <Grid size={12}>
                <SearchBox
                    prefix='c'
                    searchParams={searchParams}
                    setSearchParams={handleSearchParams}
                    searchFields={cameraSearchFields}
                    sendSearch={handleSearch}
                    sx={{margin: 'auto', mt: 2, mb: 5, maxWidth: '100%'}}
                />
            </Grid>}
            <Grid size={12}/>
            {searching && <Grid size={12}>
                <Typography variant='h6'>
                    Filtered Results:
                </Typography>
            </Grid>}
            <Grid size={12}>
                <DataGrid
                    disableColumnFilter
                    columns={viewHeaders}
                    onColumnResizeCommitted={handleColumnResize}
                    rows={searching ? filteredData : newData}
                    rowCount={(searching ? searchData.count : data.count) || 0}
                    paginationMode="server"
                    slots={{toolbar: CustomToolbar}}
                    showToolbar
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelUpdate}
                    pageSizeOptions={[PAGE_SIZE]}
                    rowHeight={80}
                    onRowClick={handleRowClick}
                    checkboxSelection
                    onRowSelectionModelChange={(model) => setRowSelected(model)}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={handleColumnVisibilityLabel}
                    slotProps={{
                        columnsManagement: {
                            disableResetButton: true,
                        },
                        toolbar: {handleReset: handleReset},
                    }}
                    sx={{
                        '& .centeredCell': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        },
                    }}
                />
            </Grid>
            {Boolean(totalPages) &&
                <Grid size={12} sx={{display: "flex", justifyContent: "flex-end", alignItems: 'center'}}>
                    <Typography sx={{mr: 1}}>Page: </Typography>
                    <Select
                        name='Page'
                        key='page'
                        onChange={handlePageSelect}
                        value={paginationModel.page}
                        sx={{
                            minWidth: 70,
                            '& .MuiSelect-select': {
                                backgroundColor: '#f9f9f9',
                            },
                        }}
                    >
                        {Array.from({length: totalPages}, (_, index) => (
                            <MenuItem key={index} value={index}>
                                {index + 1}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>}
            <PopupDialog
                open={aiModelSelect}
                display="Select AI Model"
                content={<>
                    <RelationView
                        alias='camera'
                        fields={cameraFields.relations}
                        handleChange={handleRelationChange}
                        relationName={['c_model']}
                        currentValue={{aimodel: aiModelId}}
                    />
                    <Button onClick={handleAIModelSubmit} variant="contained"
                            disabled={rowSelected.length <= 0 || !aiModelId}>Save</Button>
                </>}
                handleClose={() => setAIModelSelect(false)}
            />
            <CaptureSettings
                open={captureSettingsUpdate}
                setOpen={setCaptureSettingsUpdate}
                ids={rowSelected}
                batch
            />
        </Grid>
    )
}